import React, { useRef, useState, useEffect, useMemo } from 'react'
import useMeasure from 'react-use-measure'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import TysiaButton from 'design-system/TysiaButton/index.js'
import TysiaButtonIcon from 'design-system/TysiaButton/TysiaButtonIcon/index.js'
import ArrowLeft from 'design-system/Svgs/ArrowLeft/index.js'
import ArrowRight from 'design-system/Svgs/ArrowRight/index.js'
import TysiaText from 'design-system/TysiaText/index.js'
import { useVirtualizer } from '@tanstack/react-virtual'

let ITEM_TYPE = 'ROW' // Define drag item type

function DraggableRow({
  row,
  index,
  moveRow,
  useRowClick,
  onRowClick,
  isSelected,
  props,
}) {
  let ref = useRef(null)

  let [, drop] = useDrop({
    accept: ITEM_TYPE,
    drop: () => ({ index }),
  })

  let [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { index },
    end: (item, monitor) => {
      let dropResult = monitor.getDropResult()
      if (!dropResult || item.index === dropResult.index) return

      moveRow(item.index, dropResult.index)
    },
  })

  drag(drop(ref))

  let isLast = index === props.value.data.length - 1

  return (
    <tr
      key={row.id}
      ref={ref}
      onClick={useRowClick ? () => onRowClick(row) : null}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
        height: props.heightRow,
        color: props.colorRow,
        backgroundColor:
          typeof isSelected === 'function' && isSelected(row)
            ? props.backgroundColorRowSelected
            : !!props.onGetBackgroundRowColor &&
                typeof props.onGetBackgroundRowColor === 'function'
              ? props.onGetBackgroundRowColor(row)
              : props.backgroundColorRow,
      }}
    >
      {row.getVisibleCells().map((cell, cellIndex) => {
        // Check if this cell should be skipped due to a previous cell's colspan
        let shouldSkip = false
        for (let i = 0; i < cellIndex; i++) {
          let prevCell = row.getVisibleCells()[i]
          let prevColSpan = prevCell.column.columnDef.colSpan
          if (typeof prevColSpan === 'function') {
            let span = prevColSpan(prevCell.getContext())
            if (span > 1 && i + span > cellIndex) {
              shouldSkip = true
              break
            }
          }
        }

        if (shouldSkip) return null

        // Calculate colspan for this cell
        let colSpan = 1
        if (
          cell.column.columnDef.colSpan &&
          typeof cell.column.columnDef.colSpan === 'function'
        ) {
          colSpan = cell.column.columnDef.colSpan(cell.getContext())
        }

        return (
          <td
            key={cell.id}
            colSpan={colSpan}
            style={{
              borderBottomWidth:
                isLast && !props.borderBottomLastRow
                  ? 0
                  : props.borderBottomWidthCell,
              borderBottomStyle:
                isLast && !props.borderBottomLastRow
                  ? 'none'
                  : props.borderBottomStyleCell,
              borderBottomColor:
                isLast && !props.borderBottomLastRow
                  ? 'transparent'
                  : props.borderBottomColorCell,
              paddingLeft: props.paddingLeftCell,
              paddingRight: props.paddingRightCell,
              borderRightWidth: props.rightBorder
                ? props.borderBottomWidthCell
                : 0,
              borderRightStyle: props.rightBorder
                ? props.borderBottomStyleCell
                : 'none',
              borderRightColor: props.rightBorder
                ? props.borderBottomColorCell
                : 'transparent',
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        )
      })}
    </tr>
  )
}

function TableXPagination({
  borderColor,
  limit,
  count,
  offset,
  limitOptions,
  onLimitChange,
  onPreviousPage,
  onNextPage,
}) {
  let previousButtonProps = {
    onClick: offset === 0 ? () => null : onPreviousPage,
    opacity: offset === 0 ? 0.5 : 1,
  }

  let nextButtonProps = {
    onClick: offset + limit >= count ? () => null : onNextPage,
    opacity: offset + limit >= count ? 0.5 : 1,
  }

  // TODO: try to dock the footer
  return (
    <tfoot
      style={{
        position: 'relative',
        height: 44,
      }}
    >
      <tr
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          position: 'absolute',
          borderTopStyle: 'solid',
          borderTopWidth: 1,
          borderTopColor: borderColor,
          paddingTop: 8,
          paddingBottom: 8,
        }}
      >
        <td
          style={{
            display: 'contents',
          }}
        >
          <select
            value={limit}
            onChange={e => onLimitChange(parseInt(e.target.value))}
            style={{
              borderStyle: 'solid',
              borderWidth: 1,
              borderColor: borderColor,
              padding: 4,
              borderRadius: 4,
              marginLeft: 16,
              color: '#606060',
              outline: 'none',
              alignSelf: 'center',
            }}
          >
            {limitOptions.map(item => (
              <option key={item.id} id={item.id}>
                {item.label}
              </option>
            ))}
          </select>
          <TysiaText
            size="medium"
            text={`Showing ${offset + 1} to ${Math.min(
              offset + limit,
              count
            )} of ${count} results`}
            viewPath="/App"
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginRight: 4,
            }}
          >
            <TysiaButton
              {...previousButtonProps}
              type="ghost"
              size="small"
              viewPath="/App"
            >
              {childProps => (
                <TysiaButtonIcon {...childProps}>
                  {childProps => <ArrowLeft {...childProps} fill="none" />}
                </TysiaButtonIcon>
              )}
            </TysiaButton>
            <TysiaButton
              {...nextButtonProps}
              type="ghost"
              size="small"
              viewPath="/App"
              marginRight={8}
            >
              {childProps => (
                <TysiaButtonIcon {...childProps}>
                  {childProps => <ArrowRight {...childProps} fill="none" />}
                </TysiaButtonIcon>
              )}
            </TysiaButton>
          </div>
        </td>
      </tr>
    </tfoot>
  )
}

function FragmentComponent(props) {
  return typeof props.children === 'function'
    ? props.children(props)
    : props.children
}

function TableX(props) {
  let [ref, bounds] = useMeasure()
  let [height, setHeight] = useState(
    props.height || props.heightRow * (props.showPagination ? 3 : 2)
  )
  let [rowSelection, setRowSelection] = useState({})

  useEffect(() => {
    if (bounds.top === 0 || props.height) return

    // Always show at least 10 rows in total if there are that many.
    // If there are more records try to grow to the end of the page.
    let endOfPageHeight = window.innerHeight - bounds.top - 12
    let totalNumberOfRows =
      props.value.data.length + (props.showPagination ? 2 : 1)
    let listHeight = totalNumberOfRows * props.heightRow
    let minListHeight = Math.min(totalNumberOfRows, 10) * props.heightRow

    setHeight(Math.min(Math.max(endOfPageHeight, minListHeight), listHeight))
  }, [
    bounds,
    props.height,
    props.heightRow,
    props.showPagination,
    props.value.data.length,
  ]) //eslint-disable-line

  let table = useReactTable({
    columns: props.value.columns,
    data: props.value.data,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: props?.enableRowSelection || false,
  })

  useEffect(() => {
    if (typeof props.onChange === 'function') {
      props.onChange(table)
    }
  }, [rowSelection]) //eslint-disable-line

  useEffect(() => {
    // reset selected on new data if using enableRowSelection
    props?.enableRowSelection && setRowSelection([])
  }, [props.value.data]) //eslint-disable-line

  async function moveRow(fromIndex, toIndex) {
    if (typeof props.onReorder !== 'function') return

    let updatedData = [...props.value.data]
    let [movedItem] = updatedData.splice(fromIndex, 1)
    updatedData.splice(toIndex, 0, movedItem)

    // Update the order field based on index
    await props.onReorder(
      updatedData.map((item, index) => ({
        ...item,
        order: index + 1,
      }))
    )
  }

  let useRowClick = typeof props.onRowClick === 'function'
  let RowWrapper =
    typeof props.renderRow === 'function' ? props.renderRow : FragmentComponent
  let getRowViewPath =
    typeof props.getRowViewPath === 'function'
      ? props.getRowViewPath
      : ({ index }) => `${props.viewPath}/Row/${index}`

  let tableContent = (
    <div
      ref={ref}
      style={{
        height,
        minHeight: height,
        overflowY: 'hidden',
        marginTop: props.marginTop,
        marginLeft: props.marginLeft,
        marginRight: props.marginRight,
        marginBottom: props.marginBottom,
        borderRadius: props.borderRadius,
        borderWidth: props.borderWidth,
        borderStyle: props.borderStyle,
        borderColor: props.borderColor,
      }}
    >
      <div style={{ height: '100%', overflowY: 'auto' }}>
        <table
          style={{
            fontFamily: props.fontFamily,
            fontWeight: props.fontWeight,
            fontSize: props.fontSize,
            color: props.color,
            borderSpacing: 0,
            width: '100%',
          }}
        >
          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{
                      cursor: props.cursorHeader,
                      backgroundColor: props.backgroundColorHeader,
                      borderBottomWidth: props.borderBottomWidthHeader,
                      borderBottomStyle: props.borderBottomStyleHeader,
                      borderBottomColor:
                        !props?.borderBottomColorHeader && header.column
                          ? 'transparent'
                          : props.borderBottomColorHeader,
                      borderLeftWidth: props.borderLeftWidthHeader,
                      borderLeftStyle: props.borderLeftStyleHeader,
                      borderLeftColor: props.borderLeftColorHeader,
                      borderRightWidth: props.borderRightWidthHeader,
                      borderRightStyle: props.borderRightStyleHeader,
                      borderRightColor: props.borderRightColorHeader,
                      height: props.heightHeader,
                      fontWeight: props.fontWeightHeader,
                      paddingLeft: props.paddingLeftHeader,
                      paddingRight: props.paddingRightHeader,
                      paddingBottom: props.paddingBottomHeader,
                      width: header.column.columnDef.width || 'auto',
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    {header.isPlaceholder
                      ? null
                      : ({
                          asc: ' ↑',
                          desc: ' ↓',
                        }[header.column.getIsSorted()] ?? null)}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, i, list) => {
              let isLast = i === list.length - 1

              return props.enableDragAndDrop ? (
                <RowWrapper
                  key={`wrapper-${row.id}`}
                  data={row.original}
                  viewPath={getRowViewPath({ data: row.original, index: i })}
                >
                  <DraggableRow
                    key={row.id}
                    row={row}
                    index={i}
                    moveRow={moveRow}
                    useRowClick={useRowClick}
                    onRowClick={props.onRowClick}
                    isSelected={props.isSelected}
                    props={props}
                  />
                </RowWrapper>
              ) : (
                <RowWrapper
                  key={`wrapper-${row.id}`}
                  data={row.original}
                  viewPath={getRowViewPath({ data: row.original, index: i })}
                >
                  <tr
                    key={row.id}
                    onClick={useRowClick ? () => props.onRowClick(row) : null}
                    style={{
                      height: props.heightRow,
                      color: props.colorRow,
                      cursor: useRowClick ? 'pointer' : 'default',
                      backgroundColor:
                        typeof props.isSelected === 'function' &&
                        props.isSelected(row)
                          ? props.backgroundColorRowSelected
                          : !!props.onGetBackgroundRowColor &&
                              typeof props.onGetBackgroundRowColor ===
                                'function'
                            ? props.onGetBackgroundRowColor(row)
                            : props.backgroundColorRow,
                    }}
                  >
                    {row.getVisibleCells().map((cell, cellIndex) => {
                      // Check if this cell should be skipped due to a previous cell's colspan
                      let shouldSkip = false
                      for (let i = 0; i < cellIndex; i++) {
                        let prevCell = row.getVisibleCells()[i]
                        let prevColSpan = prevCell.column.columnDef.colSpan
                        if (typeof prevColSpan === 'function') {
                          let span = prevColSpan(prevCell.getContext())
                          if (span > 1 && i + span > cellIndex) {
                            shouldSkip = true
                            break
                          }
                        }
                      }

                      if (shouldSkip) return null

                      // Calculate colspan for this cell
                      let colSpan = 1
                      if (
                        cell.column.columnDef.colSpan &&
                        typeof cell.column.columnDef.colSpan === 'function'
                      ) {
                        colSpan = cell.column.columnDef.colSpan(
                          cell.getContext()
                        )
                      }

                      return (
                        <td
                          key={cell.id}
                          colSpan={colSpan}
                          style={{
                            borderBottomWidth:
                              isLast && !props.borderBottomLastRow
                                ? 0
                                : props.borderBottomWidthCell,
                            borderBottomStyle:
                              isLast && !props.borderBottomLastRow
                                ? 'none'
                                : props.borderBottomStyleCell,
                            borderBottomColor:
                              isLast && !props.borderBottomLastRow
                                ? 'transparent'
                                : props.borderBottomColorCell,
                            paddingLeft: props.paddingLeftCell,
                            paddingRight: props.paddingRightCell,

                            borderRightWidth: props.rightBorder
                              ? props.borderBottomWidthCell
                              : 0,
                            borderRightStyle: props.rightBorder
                              ? props.borderBottomStyleCell
                              : 'none',
                            borderRightColor: props.rightBorder
                              ? props.borderBottomColorCell
                              : 'transparent',
                            paddingBottom: props.paddingBottomCell || 0,
                            paddingTop: props.paddingTopCell || 0,
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      )
                    })}
                  </tr>
                </RowWrapper>
              )
            })}
          </tbody>
          {props.showPagination && (
            <TableXPagination borderColor={props.borderColor} {...props} />
          )}
        </table>
      </div>
    </div>
  )

  return props.enableDragAndDrop ? (
    <DndProvider backend={HTML5Backend}>{tableContent}</DndProvider>
  ) : (
    tableContent
  )
}

function TableXVirtualized(props) {
  // parity with non-virtual
  let [rowSelection, setRowSelection] = useState({})
  let table = useReactTable({
    columns: props.value.columns,
    data: props.value.data,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: props?.enableRowSelection || false,
  })

  useEffect(() => {
    if (typeof props.onChange === 'function') props.onChange(table)
  }, [rowSelection]) //eslint-disable-line

  useEffect(() => {
    props?.enableRowSelection && setRowSelection([])
  }, [props.value.data]) //eslint-disable-line

  // same auto-height logic
  let [ref, bounds] = useMeasure()
  let [height, setHeight] = useState(
    props.height || props.heightRow * (props.showPagination ? 3 : 2)
  )
  useEffect(() => {
    if (bounds.top === 0 || props.height) return
    let endOfPageHeight = window.innerHeight - bounds.top - 12
    let totalNumberOfRows =
      props.value.data.length + (props.showPagination ? 2 : 1)
    let listHeight = totalNumberOfRows * props.heightRow
    let minListHeight = Math.min(totalNumberOfRows, 10) * props.heightRow
    setHeight(Math.min(Math.max(endOfPageHeight, minListHeight), listHeight))
  }, [
    bounds,
    props.height,
    props.heightRow,
    props.showPagination,
    props.value.data.length,
  ])

  // virtualization
  let tableContainerRef = useRef(null)
  let rows = table.getRowModel().rows
  let rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => props.heightRow,
    overscan: 8,
  })

  let useRowClick = typeof props.onRowClick === 'function'
  let RowWrapper =
    typeof props.renderRow === 'function' ? props.renderRow : FragmentComponent
  let getRowViewPath =
    typeof props.getRowViewPath === 'function'
      ? props.getRowViewPath
      : ({ index }) => `${props.viewPath}/Row/${index}`

  async function moveRow(fromIndex, toIndex) {
    if (typeof props.onReorder !== 'function') return
    let updatedData = [...props.value.data]
    let [movedItem] = updatedData.splice(fromIndex, 1)
    updatedData.splice(toIndex, 0, movedItem)
    await props.onReorder(
      updatedData.map((item, index) => ({ ...item, order: index + 1 }))
    )
  }

  function renderCells(row, isLast) {
    let cells = row.getVisibleCells()
    return cells.map((cell, cellIndex) => {
      let shouldSkip = false
      for (let i = 0; i < cellIndex; i++) {
        let prevCell = cells[i]
        let prevColSpan = prevCell.column.columnDef.colSpan
        if (typeof prevColSpan === 'function') {
          let span = prevColSpan(prevCell.getContext())
          if (span > 1 && i + span > cellIndex) {
            shouldSkip = true
            break
          }
        }
      }
      if (shouldSkip) return null

      let colSpan = 1
      if (
        cell.column.columnDef.colSpan &&
        typeof cell.column.columnDef.colSpan === 'function'
      ) {
        colSpan = cell.column.columnDef.colSpan(cell.getContext())
      }

      return (
        <td
          key={cell.id}
          colSpan={colSpan}
          style={{
            borderBottomWidth:
              isLast && !props.borderBottomLastRow
                ? 0
                : props.borderBottomWidthCell,
            borderBottomStyle:
              isLast && !props.borderBottomLastRow
                ? 'none'
                : props.borderBottomStyleCell,
            borderBottomColor:
              isLast && !props.borderBottomLastRow
                ? 'transparent'
                : props.borderBottomColorCell,
            paddingLeft: props.paddingLeftCell,
            paddingRight: props.paddingRightCell,
            borderRightWidth: props.rightBorder
              ? props.borderBottomWidthCell
              : 0,
            borderRightStyle: props.rightBorder
              ? props.borderBottomStyleCell
              : 'none',
            borderRightColor: props.rightBorder
              ? props.borderBottomColorCell
              : 'transparent',
            paddingBottom: props.paddingBottomCell || 0,
            paddingTop: props.paddingTopCell || 0,
          }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      )
    })
  }

  function Header() {
    return (
      <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <th
                key={header.id}
                colSpan={header.colSpan}
                onClick={header.column.getToggleSortingHandler()}
                style={{
                  cursor: props.cursorHeader,
                  backgroundColor: props.backgroundColorHeader,
                  borderBottomWidth: props.borderBottomWidthHeader,
                  borderBottomStyle: props.borderBottomStyleHeader,
                  borderBottomColor:
                    !props?.borderBottomColorHeader && header.column
                      ? 'transparent'
                      : props.borderBottomColorHeader,
                  borderLeftWidth: props.borderLeftWidthHeader,
                  borderLeftStyle: props.borderLeftStyleHeader,
                  borderLeftColor: props.borderLeftColorHeader,
                  borderRightWidth: props.borderRightWidthHeader,
                  borderRightStyle: props.borderRightStyleHeader,
                  borderRightColor: props.borderRightColorHeader,
                  height: props.heightHeader,
                  fontWeight: props.fontWeightHeader,
                  paddingLeft: props.paddingLeftHeader,
                  paddingRight: props.paddingRightHeader,
                  paddingBottom: props.paddingBottomHeader,
                  width: header.column.columnDef.width || 'auto',
                }}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                {header.isPlaceholder
                  ? null
                  : ({
                      asc: ' ↑',
                      desc: ' ↓',
                    }[header.column.getIsSorted()] ?? null)}
              </th>
            ))}
          </tr>
        ))}
      </thead>
    )
  }

  // spacer rows keep proper table layout
  let virtualItems = rowVirtualizer.getVirtualItems()
  let totalSize = rowVirtualizer.getTotalSize()
  let paddingTop = virtualItems.length ? virtualItems[0].start : 0
  let last = virtualItems.length ? virtualItems[virtualItems.length - 1] : null
  let paddingBottom = last ? totalSize - (last.start + last.size) : 0
  let leafColCount = table.getAllLeafColumns().length

  let TableBody = (
    <tbody>
      {paddingTop > 0 && (
        <tr>
          <td
            colSpan={leafColCount}
            style={{ height: paddingTop, padding: 0, border: 0 }}
          />
        </tr>
      )}

      {virtualItems.map(vi => {
        let row = rows[vi.index]
        let isLast = vi.index === rows.length - 1

        if (props.enableDragAndDrop) {
          return (
            <RowWrapper
              key={`wrapper-${row.id}`}
              data={row.original}
              viewPath={getRowViewPath({ data: row.original, index: vi.index })}
            >
              <DraggableRow
                key={row.id}
                row={row}
                index={vi.index}
                moveRow={moveRow}
                useRowClick={useRowClick}
                onRowClick={props.onRowClick}
                isSelected={props.isSelected}
                props={props}
              />
            </RowWrapper>
          )
        }

        return (
          <RowWrapper
            key={`wrapper-${row.id}`}
            data={row.original}
            viewPath={getRowViewPath({ data: row.original, index: vi.index })}
          >
            <tr
              key={row.id}
              onClick={useRowClick ? () => props.onRowClick(row) : null}
              style={{
                height: props.heightRow,
                color: props.colorRow,
                cursor: useRowClick ? 'pointer' : 'default',
                backgroundColor:
                  typeof props.isSelected === 'function' &&
                  props.isSelected(row)
                    ? props.backgroundColorRowSelected
                    : !!props.onGetBackgroundRowColor &&
                        typeof props.onGetBackgroundRowColor === 'function'
                      ? props.onGetBackgroundRowColor(row)
                      : props.backgroundColorRow,
              }}
            >
              {renderCells(row, isLast)}
            </tr>
          </RowWrapper>
        )
      })}

      {paddingBottom > 0 && (
        <tr>
          <td
            colSpan={leafColCount}
            style={{ height: paddingBottom, padding: 0, border: 0 }}
          />
        </tr>
      )}
    </tbody>
  )

  let tableEl = (
    <table
      style={{
        fontFamily: props.fontFamily,
        fontWeight: props.fontWeight,
        fontSize: props.fontSize,
        color: props.color,
        borderSpacing: 0,
        width: '100%',
      }}
    >
      <Header />
      {TableBody}
      {props.showPagination && (
        <TableXPagination borderColor={props.borderColor} {...props} />
      )}
    </table>
  )

  let content = (
    <div
      id="virtual-table"
      ref={tableContainerRef}
      style={{
        height,
        minHeight: height,
        overflowY: 'auto',
        marginTop: props.marginTop,
        marginLeft: props.marginLeft,
        marginRight: props.marginRight,
        marginBottom: props.marginBottom,
        borderRadius: props.borderRadius,
        borderWidth: props.borderWidth,
        borderStyle: props.borderStyle,
        borderColor: props.borderColor,
      }}
    >
      <div ref={ref} style={{ height: '100%' }}>
        {tableEl}
      </div>
    </div>
  )

  return props.enableDragAndDrop ? (
    <DndProvider backend={HTML5Backend}>{content}</DndProvider>
  ) : (
    content
  )
}

export default function Table({
  backgroundColorHeader = '#F5F7FA',
  backgroundColorRow = 'transparent',
  backgroundColorRowSelected = '#F5F7FA',
  borderBottomLastRow = false,
  borderBottomWidthCell = 1,
  borderBottomWidthHeader = 1,
  borderBottomStyleCell = 'solid',
  borderBottomStyleHeader = 'solid',
  borderBottomColorCell = '#E4E7EB',
  borderBottomColorHeader = '#E4E7EB',
  borderLeftWidthHeader = 1,
  borderLeftStyleHeader = 'solid',
  borderLeftColorHeader = '#F5F7FA',
  borderRightWidthHeader = 0,
  borderRightStyleHeader = 'solid',
  borderRightColorHeader = '#F5F7FA',
  borderColor = '#E4E7EB',
  borderRadius = 8,
  borderStyle = 'solid',
  borderWidth = 1,
  color = '#152935',
  colorRow = '#152935',
  cursorHeader = 'pointer',
  fontFamily = 'Inter',
  fontSize = 12,
  fontWeight = 400,
  fontWeightHeader = 500,
  heightHeader = 36,
  heightRow = 44,
  marginBottom = 0,
  marginLeft = 0,
  marginRight = 0,
  marginTop = 16,
  overflow = 'hidden',
  paddingLeftCell = 16,
  paddingLeftHeader = 16,
  paddingRightCell = 16,
  paddingRightHeader = 16,
  onGetBackgroundRowColor = null,
  ...props // everything else
}) {
  let allProps = {
    backgroundColorHeader,
    backgroundColorRow,
    backgroundColorRowSelected,
    borderBottomLastRow,
    borderBottomWidthCell,
    borderBottomWidthHeader,
    borderBottomStyleCell,
    borderBottomStyleHeader,
    borderBottomColorCell,
    borderBottomColorHeader,
    borderLeftWidthHeader,
    borderLeftStyleHeader,
    borderLeftColorHeader,
    borderRightWidthHeader,
    borderRightStyleHeader,
    borderRightColorHeader,
    borderColor,
    borderRadius,
    borderStyle,
    borderWidth,
    color,
    colorRow,
    cursorHeader,
    fontFamily,
    fontSize,
    fontWeight,
    fontWeightHeader,
    heightHeader,
    heightRow,
    marginBottom,
    marginLeft,
    marginRight,
    marginTop,
    overflow,
    paddingLeftCell,
    paddingLeftHeader,
    paddingRightCell,
    paddingRightHeader,
    onGetBackgroundRowColor,
    ...props,
  }

  return !props.value ||
    !props.value?.columns ||
    !props.value?.data ? null : props.virtual ? (
    <TableXVirtualized {...allProps}></TableXVirtualized>
  ) : (
    <TableX {...allProps}></TableX>
  )
}
